// The music director: the Godot score, wired to the game's states, plus the ambience beds.
//
// Music streams through two HTMLAudioElement "decks" into WebAudio (MediaElementSource), so
// a four-minute track costs a network stream and a few hundred KB of decoder state instead of
// ~90 MB of decoded PCM. Changes crossfade equal-power over 3 s (the Godot MusicPlayer's
// FADE_DURATION, raised there after "shorter transitions felt jarring"). Both decks are
// play()/pause()d once inside the unlocking user gesture, which is what lets Safari start
// them later from game code.
//
// States (what the Godot game played, scripts/main.gd, extraction_sequence.gd, cerberus.gd):
//   saloon   "wiggly thing", looped, under the saloon car's room beds and one-shots
//   island   the three extended "stolen theme" tracks, shuffled, cycling, -6 dB, over the
//            arena ambience whose combat layer follows setIntensity()
//   boss     "The Last Can" once (after the boss sting has landed), then the extended cut looped
//   extract  the four extraction swells: the two mashups in random order, then the originals
//   victory  the victory sting, then the calm theme
//   defeat   the defeat sting, then silence
//   calm     "stolen theme (basic)", the quiet one
//   off      fade to silence
// Cues ('sendoff', the 15.4 s roof-launch score written to its timeline) start on the frame,
// with no crossfade, and duck the bed until they end.
//
// A 250 ms timer (not the render loop) watches track ends, so the director keeps working in
// menus and while the tab is backgrounded.

export type MusicState = 'off' | 'saloon' | 'island' | 'boss' | 'extract' | 'victory' | 'defeat' | 'calm';

interface Plan {
  /** tracks in order; after the last: loop the last one (`loopLast`) or wrap to `wrapTo` */
  tracks: string[];
  loopLast?: boolean;
  wrapTo?: number;
  /** linear gain for this state's bed */
  level: number;
  /** seconds to wait before the first track (e.g. let the boss sting land) */
  delay?: number;
  /** state to move to when the plan runs out (no loop) */
  then?: MusicState;
  /** fade-in for the first track (a sting wants its hit, not a 3 s swell) */
  fadeIn?: number;
}

const FADE = 3.0;
const SAL_BEDS: [string, number][] = [['sal_room', 0.9], ['sal_bogie', 0.85], ['sal_rail', 0.5], ['sal_bleed', 0.45], ['sal_radio', 0.38], ['sal_murmur', 0.55]];

function shuffle<T>(a: T[]) { for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; }

class Deck {
  el: HTMLAudioElement;
  node: MediaElementAudioSourceNode;
  gain: GainNode;
  track = '';
  constructor(ctx: AudioContext, out: AudioNode) {
    this.el = new Audio();
    this.el.preload = 'auto';
    this.el.crossOrigin = 'anonymous';
    this.node = ctx.createMediaElementSource(this.el);
    this.gain = ctx.createGain();
    this.gain.gain.value = 0;
    this.node.connect(this.gain); this.gain.connect(out);
  }
}

export class Music {
  private ctx: AudioContext | null = null;
  private out!: GainNode;
  private bus!: GainNode;
  private base = '/audio/';
  private decks: Deck[] = [];
  private front = 0;
  private state: MusicState = 'off';
  private wanted: MusicState = 'off';
  private plan: Plan | null = null;
  private idx = 0;
  private planStart = 0;
  private started = false;
  private fading = false;
  private cueEl: HTMLAudioElement | null = null;
  private cueTimer: ReturnType<typeof setTimeout> | undefined;
  private manifestMusic: Record<string, number> = {};
  private cueDuck!: GainNode;
  private buf!: (stem: string) => AudioBuffer | null;
  private load!: (stem: string) => Promise<void>;
  private beds = new Map<string, { src: AudioBufferSourceNode; gain: GainNode }>();
  private ambGain!: GainNode;
  private intensity = 0;
  private salOs: string[] = [];
  private nextOs = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private unlocked = false;

  /** `out` is the music bus, `amb` the ambience bus (separate, so music at 0 keeps the world) */
  attach(ctx: AudioContext, out: GainNode, amb: GainNode, base: string, buf: (stem: string) => AudioBuffer | null, load: (stem: string) => Promise<void>) {
    this.ctx = ctx; this.bus = out; this.base = base; this.buf = buf; this.load = load;
    this.cueDuck = ctx.createGain(); this.cueDuck.connect(out);
    this.out = ctx.createGain(); this.out.connect(this.cueDuck);
    this.ambGain = ctx.createGain(); this.ambGain.gain.value = 1; this.ambGain.connect(amb);
    // the cue deck exists from the start so the unlocking gesture can bless it (Safari)
    this.cueEl = new Audio(); this.cueEl.crossOrigin = 'anonymous'; this.cueEl.preload = 'auto';
    const cn = ctx.createMediaElementSource(this.cueEl), cg = ctx.createGain(); cg.gain.value = 0.9;
    cn.connect(cg); cg.connect(out); // past the duck: the cue is what ducks the bed
    this.decks = [new Deck(ctx, this.out), new Deck(ctx, this.out)];
    this.timer = setInterval(() => this.tick(), 250);
    if (this.wanted !== 'off') this.enter(this.wanted);
  }

  setManifest(j: { saloon_os?: string[]; music?: Record<string, number> }) { this.salOs = j.saloon_os ?? []; this.manifestMusic = j.music ?? {}; }

  /** call inside a user gesture: lets Safari start the decks from code later */
  unlockElements() {
    if (this.unlocked || !this.decks.length) return;
    this.unlocked = true;
    for (const d of this.decks) {
      d.el.src = this.base + 'music/m_victory.mp3';
      d.el.muted = true;
      const p = d.el.play();
      if (p) p.then(() => { d.el.pause(); d.el.muted = false; if (!d.track) d.el.removeAttribute('src'); }).catch(() => { d.el.muted = false; });
    }
    const ce = this.cueEl!;
    ce.src = this.base + 'music/m_victory.mp3'; ce.muted = true;
    const p = ce.play();
    if (p) p.then(() => { ce.pause(); ce.muted = false; }).catch(() => { ce.muted = false; });
    if (this.wanted !== 'off') setTimeout(() => this.enter(this.wanted), 60);
  }

  private planFor(s: MusicState): Plan | null {
    switch (s) {
      case 'saloon': return { tracks: ['m_saloon'], loopLast: true, level: 0.75 };
      case 'island': return { tracks: shuffle(['m_island_1', 'm_island_3', 'm_island_4']), wrapTo: 0, level: 0.7 };
      case 'boss': return { tracks: ['m_boss_intro', 'm_boss_loop'], loopLast: true, level: 0.85, delay: 3.0, fadeIn: 0.15 };
      case 'extract': return { tracks: [...shuffle(['m_extract_3', 'm_extract_4']), ...shuffle(['m_extract_1', 'm_extract_2'])], wrapTo: 0, level: 0.85 };
      case 'victory': return { tracks: ['m_victory', 'm_calm'], loopLast: true, level: 0.85, fadeIn: 0.15 };
      case 'defeat': return { tracks: ['m_defeat'], level: 0.85, then: 'off', fadeIn: 0.15 };
      case 'calm': return { tracks: ['m_calm'], loopLast: true, level: 0.6 };
      default: return null;
    }
  }

  /** what the game wants; safe to call every frame (a one-shot plan never replays while wanted) */
  setState(s: MusicState, force = false) {
    if (s === this.wanted && !force) return;
    this.wanted = s;
    this.enter(s);
  }

  /** run a state's plan (also used for a plan's own `then`, which leaves `wanted` alone) */
  private enter(s: MusicState) {
    if (!this.ctx) return;
    this.state = s;
    this.plan = this.planFor(s);
    this.idx = 0;
    this.started = false;
    this.planStart = this.ctx.currentTime;
    if (!this.plan) this.fadeOutAll();
    else if (!this.plan.delay) this.startTrack();
    else this.fadeOutAll(this.plan.delay);
    this.syncBeds();
  }

  /** island combat 0..1: lifts the arena combat layer, thins the calm bed */
  setIntensity(v: number) {
    this.intensity = Math.max(0, Math.min(1, v));
    if (!this.ctx || this.state !== 'island') return;
    const now = this.ctx.currentTime;
    this.bedLevel('amb_arena', 0.55 * (1 - 0.45 * this.intensity), now, 1.2);
    this.bedLevel('amb_combat', 0.6 * Math.pow(this.intensity, 1.3), now, 1.0);
  }

  cue(name: 'sendoff') {
    const ctx = this.ctx, el = this.cueEl;
    if (!ctx || !el) return;
    const file = name === 'sendoff' ? 'm_sendoff' : name;
    const restore = () => { if (this.ctx) { this.cueDuck.gain.cancelScheduledValues(this.ctx.currentTime); this.cueDuck.gain.setTargetAtTime(1, this.ctx.currentTime, 0.8); } };
    el.src = this.base + 'music/' + file + '.mp3';
    el.currentTime = 0;
    el.onended = restore;
    el.onerror = restore;
    const p = el.play();
    if (p) p.catch(restore);
    const now = ctx.currentTime;
    this.cueDuck.gain.cancelScheduledValues(now);
    this.cueDuck.gain.setTargetAtTime(0.12, now, 0.08);
    // belt and braces: never leave the bed ducked past the cue's length
    clearTimeout(this.cueTimer);
    this.cueTimer = setTimeout(restore, ((this.manifestMusic[file] ?? 16) + 1) * 1000);
  }

  private deckFront() { return this.decks[this.front]; }

  private startTrack() {
    const ctx = this.ctx, plan = this.plan;
    if (!ctx || !plan || !this.decks.length) return;
    const track = plan.tracks[this.idx];
    const next = this.decks[1 - this.front], prev = this.deckFront();
    const now = ctx.currentTime;
    next.track = track;
    next.el.loop = !!plan.loopLast && this.idx === plan.tracks.length - 1;
    next.el.src = this.base + 'music/' + track + '.mp3';
    next.el.currentTime = 0;
    const p = next.el.play();
    if (p) p.catch(() => { /* locked until a gesture: unlockElements() retries the state */ });
    // equal-power crossfade
    const curve = (up: boolean) => { const c = new Float32Array(32); for (let i = 0; i < 32; i++) { const t = i / 31; c[i] = up ? Math.sin(t * Math.PI / 2) : Math.cos(t * Math.PI / 2); } return c; };
    const fin = this.idx === 0 && plan.fadeIn ? plan.fadeIn : FADE;
    next.gain.gain.cancelScheduledValues(now);
    next.gain.gain.setValueAtTime(0.0001, now);
    try { next.gain.gain.setValueCurveAtTime(curve(true).map((v) => v * plan.level), now, fin); } catch { next.gain.gain.linearRampToValueAtTime(plan.level, now + fin); }
    if (prev.track) {
      const g = prev.gain.gain.value;
      prev.gain.gain.cancelScheduledValues(now);
      prev.gain.gain.setValueAtTime(g, now);
      try { prev.gain.gain.setValueCurveAtTime(curve(false).map((v) => v * g), now, FADE); } catch { prev.gain.gain.linearRampToValueAtTime(0, now + FADE); }
      const el = prev.el, was = prev.track;
      setTimeout(() => { if (prev.track === was && this.deckFront() !== prev) { el.pause(); prev.track = ''; } }, FADE * 1000 + 100);
    }
    this.front = 1 - this.front;
    this.started = true;
    this.fading = false;
  }

  private fadeOutAll(over = FADE) {
    const ctx = this.ctx;
    if (!ctx) return;
    const now = ctx.currentTime;
    for (const d of this.decks) {
      if (!d.track) continue;
      d.gain.gain.cancelScheduledValues(now);
      d.gain.gain.setValueAtTime(d.gain.gain.value, now);
      d.gain.gain.linearRampToValueAtTime(0, now + Math.min(over, FADE));
      const el = d.el, was = d.track;
      setTimeout(() => { if (d.track === was && (this.state !== this.wanted || !this.started || this.deckFront() !== d)) { el.pause(); d.track = ''; } }, Math.min(over, FADE) * 1000 + 100);
    }
  }

  private tick() {
    const ctx = this.ctx, plan = this.plan;
    if (!ctx) return;
    if (this.state === 'saloon') this.saloonOneShots();
    if (!plan) return;
    if (!this.started) {
      if (ctx.currentTime - this.planStart >= (plan.delay ?? 0)) this.startTrack();
      return;
    }
    const d = this.deckFront();
    if (!d.track || d.el.loop || this.fading) return;
    const dur = d.el.duration;
    if (!isFinite(dur) || dur <= 0) return;
    const left = dur - d.el.currentTime;
    const last = this.idx >= plan.tracks.length - 1;
    if (left < FADE + 0.3) {
      if (!last) { this.idx++; this.fading = true; this.startTrack(); }
      else if (plan.wrapTo !== undefined) { this.idx = plan.wrapTo; this.fading = true; this.startTrack(); }
      else if (d.el.ended || left < 0.3) { this.fading = true; if (plan.then) this.enter(plan.then); }
    }
  }

  // ---------------------------------------------------------------- ambience beds
  private syncBeds() {
    const ctx = this.ctx;
    if (!ctx) return;
    const now = ctx.currentTime;
    const want = new Map<string, number>();
    if (this.state === 'island' || this.state === 'boss') { want.set('amb_arena', 0.55 * (1 - 0.45 * this.intensity)); want.set('amb_combat', 0.6 * Math.pow(this.intensity, 1.3)); }
    if (this.state === 'boss') want.set('amb_combat', 0.5);
    if (this.state === 'saloon') for (const [k, v] of SAL_BEDS) want.set(k, v);
    for (const [k, b] of this.beds) {
      if (want.has(k)) continue;
      b.gain.gain.setTargetAtTime(0, now, 0.8);
      // stop it once it has faded, unless a state change wanted it back meanwhile
      setTimeout(() => { if (this.beds.get(k) === b && !this.bedWanted(k)) { try { b.src.stop(); } catch { /* */ } b.gain.disconnect(); this.beds.delete(k); } }, 5000);
    }
    for (const [k, v] of want) this.bedLevel(k, v, now, 1.0);
  }

  private bedWanted(stem: string) {
    return ((this.state === 'island' || this.state === 'boss') && stem.startsWith('amb_')) || (this.state === 'saloon' && stem.startsWith('sal_'));
  }

  private bedLevel(stem: string, level: number, now: number, tc: number) {
    let b = this.beds.get(stem);
    if (!b) {
      const buf = this.buf(stem);
      if (!buf) { void this.load(stem).then(() => { if (this.buf(stem)) this.syncBeds(); }); return; }
      const ctx = this.ctx!;
      const src = ctx.createBufferSource(); src.buffer = buf; src.loop = true;
      const gain = ctx.createGain(); gain.gain.value = 0;
      src.connect(gain); gain.connect(this.ambGain);
      src.start(now, Math.random() * buf.duration);
      b = { src, gain };
      this.beds.set(stem, b);
    }
    b.gain.gain.setTargetAtTime(level, now, tc);
  }

  /** the room lives: glasses, bottles, a laugh, a chair, the hull ticking, every few seconds */
  private saloonOneShots() {
    const ctx = this.ctx!;
    if (!this.salOs.length || ctx.currentTime < this.nextOs) return;
    this.nextOs = ctx.currentTime + 2.2 + Math.random() * 5.5;
    const stem = this.salOs[Math.floor(Math.random() * this.salOs.length)];
    const b = this.buf(stem);
    if (!b) { void this.load(stem); return; }
    const src = ctx.createBufferSource(); src.buffer = b;
    src.playbackRate.value = 0.94 + Math.random() * 0.12;
    const g = ctx.createGain(); g.gain.value = 0.18 + Math.random() * 0.2;
    const p = ctx.createStereoPanner(); p.pan.value = Math.random() * 1.6 - 0.8;
    src.connect(g); g.connect(p); p.connect(this.ambGain);
    src.onended = () => p.disconnect();
    src.start();
  }

  debug() {
    return { state: this.state, track: this.deckFront()?.track ?? '', t: this.deckFront()?.el.currentTime ?? 0, paused: this.deckFront()?.el.paused ?? true, beds: [...this.beds.keys()], cue: this.cueEl && !this.cueEl.paused ? this.cueEl.src.split('/').pop() ?? '' : '' };
  }

  dispose() { if (this.timer) clearInterval(this.timer); }
}
